import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useRemoveMemberBulkDialogContent } from "@components/table/members/bulk-actions/bulk-remove-member-helper";
import {
  BaseCompanyIdProps,
  BaseGroupIdProps,
  BaseProjectIdProps,
  BaseTeamIdProps,
} from "@custom-types/base-types";
import { BaseMemberAndTeam } from "@custom-types/member-types";
import { removeMemberFromGroup } from "@store/groups/groups-slice-thunk";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedProjectManagerSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import {
  removeMembersByEmails,
  removeMembersByIds,
} from "@store/teams/teams-slice";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";

interface BaseRemoveProps extends BaseCompanyIdProps {
  /**
   * Title for the bulk action button.
   * @default "members" - The button will display "Remove members".
   * Optionally, it can be customized, e.g., "from workspace", displaying "Remove from workspace".
   */
  title?: string;
}
// All types are needed to be able to use the component in different contexts.
// However, "never" type is used to make sure that the irrelevant keys should not be passed.
interface RemoveFromCompanyProps extends BaseRemoveProps {
  /** The table subject type of the table */
  subjectType: "workspace";

  projectId?: never;
  groupId?: never;
  onGroupRemoved?: never;
  teamId?: never;
}

interface RemoveFromProjectProps extends BaseRemoveProps, BaseProjectIdProps {
  /** The table subject of the table */
  subjectType: "project";

  groupId?: never;
  onGroupRemoved?: never;
  teamId?: never;
}

interface RemoveFromGroupProps extends BaseRemoveProps, BaseGroupIdProps {
  /** The table subject of the table */
  subjectType: "group";

  /** Callback when the group is removed */
  onGroupRemoved: () => void;

  projectId?: never;
  teamId?: never;
}

interface RemoveFromTeamProps extends BaseRemoveProps, BaseTeamIdProps {
  /** The table subject type of the table */
  subjectType: "team";

  projectId?: never;
  groupId?: never;
  onGroupRemoved?: never;
}

interface RemoveFromWorkspaceTeamProps
  extends BaseRemoveProps,
    BaseTeamIdProps {
  /** The table subject type of the table */
  subjectType: "workspaceTeam";

  projectId?: never;
  groupId?: never;
  onGroupRemoved?: never;
}

/** All the possible options to remove a member from context */
type RemoveMember =
  | RemoveFromCompanyProps
  | RemoveFromProjectProps
  | RemoveFromGroupProps
  | RemoveFromTeamProps
  | RemoveFromWorkspaceTeamProps;

/** Show remove member button and dispatch action where the bulk dialog will act accordingly */
export function BulkRemoveMember({
  companyId,
  projectId,
  groupId,
  teamId,
  subjectType,
  onGroupRemoved,
  title = "Remove members",
}: RemoveMember): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);

  const { user: selectedProjectManager } = useAppSelector(
    selectedProjectManagerSelector
  );
  const bulkActionName = useAppSelector(bulkActionNameSelector);

  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );

  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const selectedTeams = useAppSelector(selectedEntitiesSelector("teams"));

  const { bulkDialogContent } = useRemoveMemberBulkDialogContent(subjectType);

  // TODO: https://faro01.atlassian.net/browse/ST-2840 remove this variable after remove the user group on members endpoint
  const selectedEntitiesWithoutTeams = selectedEntities.filter(
    (entities) => entities.kind === "user"
  );

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onRemoveMemberConfirmed(
    member: BaseMemberAndTeam
  ): Promise<unknown> {
    switch (subjectType) {
      case "workspace":
        return dispatch(
          removeMemberFromCompany({
            companyId,
            member,
          })
        )
          .unwrap()
          .then((response) => {
            return dispatch(removeMembersByEmails([response.email]));
          });

      case "workspaceTeam":
        return dispatch(
          removeMemberFromCompany({
            companyId,
            member,
          })
        )
          .unwrap()
          .then(() => {
            return dispatch(removeMembersByIds([member.identity]));
          });

      case "project":
        return dispatch(
          removeMemberFromProject({
            companyId,
            member,
            projectId,
          })
        ).unwrap();

      case "group":
        return dispatch(
          removeMemberFromGroup({
            companyId,
            member,
            groupId,
            onGroupRemoved,
          })
        ).unwrap();

      case "team":
        return dispatch(
          removeMembersFromTeam({
            companyId,
            teamId,
            memberIds: [member.identity],
          })
        ).unwrap();
    }
  }

  function onRemoveMemberClick(): void {
    dispatch(setBulkActionName("removeMember"));

    if (subjectType === "project" && selectedProjectManager) {
      dispatch(
        updateOne({
          id: selectedProjectManager.identity,
          changes: {
            status: "not-allowed",
            message: "Cannot remove owner of project",
          },
        })
      );
    }

    setShouldShowDialog(true);
  }

  /** Clear the bulk action names and close the dialog */
  function onSuccessfulComplete(): void {
    dispatch(setBulkActionName(null));
    setShouldShowDialog(false);
  }

  return (
    <>
      <FaroTextButton
        onClick={onRemoveMemberClick}
        tooltipText={`Remove member from ${subjectType}`}
        sx={{ fontWeight: 600 }}
      >
        {title}
      </FaroTextButton>

      {bulkActionName === "removeMember" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkRemoveMember}
          bulkActionCallback={onRemoveMemberConfirmed}
          onSuccessfulComplete={onSuccessfulComplete}
          selectedEntities={[...selectedEntitiesWithoutTeams, ...selectedTeams]}
          uniqueIdKey="identity"
          tableDataType="members"
        />
      )}
    </>
  );
}
